import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';
import { type EntityType } from '~/types/entity';

import { promiseAllThrottled } from '~/utils/promise';

import { addToOrganizationalUnit } from './useMutationAddToOrganizationalUnit';
import { deleteFromOrganizationalUnit } from './useMutationDeleteFromOrganizationalUnit';

/**
 * Type of organizational unit update operation.
 * - updateParentOrganizationalUnits: Adds new units before deleting old ones
 * - updateOrganizationalUnitEntities: Deletes old units before adding new ones
 */
type UpdateType =
  | 'updateParentOrganizationalUnits'
  | 'updateOrganizationalUnitEntities';

/**
 * Parameters for updating organizational units of an entity.
 * @property {UUID[]} addedMembers - Array of organizational unit IDs to add
 * @property {UUID[]} deletedMembers - Array of organizational unit IDs to remove
 * @property {UUID} entityId - ID of the entity being updated
 * @property {string} entityType - Type of entity (e.g., 'user')
 * @property {UpdateType} updateType - Determines the order of add/delete operations
 */
export type UpdateOrganizationalUnitsParams = {
  addedMembers: UUID[];
  deletedMembers: UUID[];
  entityId: UUID;
  entityType: EntityType;
  updateType: UpdateType;
};

type UpdateOrganizationalUnitsMutationOptions = UseMutationOptions<
  void,
  Error,
  UpdateOrganizationalUnitsParams
>;

export type UpdateOrganizationalUnitsMutateAsyncFunction =
  UseMutateAsyncFunction<void, Error, UpdateOrganizationalUnitsParams>;

/**
 * Updates the organizational unit memberships for an entity by making API calls to add and remove members.
 *
 * @param {UpdateOrganizationalUnitsParams} params - Parameters for the update operation
 * @throws {Error} If the API calls fail
 * @returns {Promise<void>}
 *
 * @remarks
 * The order of operations (add then delete, or delete then add) depends on the updateType:
 * - For 'updateParentOrganizationalUnits': Adds new units first to prevent having an empty list
 * - For 'updateOrganizationalUnitEntities': Removes old units first before adding new ones
 */
const updateOrganizationalUnits = async ({
  addedMembers,
  deletedMembers,
  entityType,
  entityId,
  updateType = 'updateOrganizationalUnitEntities',
}: UpdateOrganizationalUnitsParams) => {
  if (updateType === 'updateParentOrganizationalUnits') {
    await promiseAllThrottled(
      addedMembers.map(async (parentOrganizationalUnitId) =>
        addToOrganizationalUnit(
          parentOrganizationalUnitId,
          entityType,
          entityId,
        ),
      ),
    );

    // Delete items only after all add actions are done -> prevent case where item doesn't have >= 1 parent OU.
    await promiseAllThrottled(
      deletedMembers.map(async (parentOrganizationalUnitId) =>
        deleteFromOrganizationalUnit(
          parentOrganizationalUnitId,
          entityType,
          entityId,
        ),
      ),
    );
  } else {
    await promiseAllThrottled(
      addedMembers.map(async (memberId) =>
        addToOrganizationalUnit(entityId, entityType, memberId),
      ),
    );

    // Delete items only after all add actions are done -> prevent case where item doesn't have >= 1 parent OU.
    await promiseAllThrottled(
      deletedMembers.map(async (memberId) =>
        deleteFromOrganizationalUnit(entityId, entityType, memberId),
      ),
    );
  }
};

/**
 * Custom hook for managing organizational unit updates using react-query mutations.
 *
 * @param {Parameters<typeof useMutation>[0]} [options] - Optional react-query mutation options
 * @returns {UseMutationResult} Mutation object for handling the update operation
 *
 * @example
 * ```tsx
 * const mutation = useMutationUpdateOrganizationalUnits();
 *
 * const handleUpdate = () => {
 *   mutation.mutate({
 *     addedMembers: ['new1', 'new2'],
 *     deletedMembers: ['old1', 'old2'],
 *     entityId: '123',
 *     entityType: 'user',
 *     updateType: 'updateOrganizationalUnitEntities',
 *   });
 * };
 * ```
 */
export const useMutationUpdateOrganizationalUnits = (
  options?: UpdateOrganizationalUnitsMutationOptions,
) =>
  useMutation({
    async mutationFn({
      addedMembers,
      deletedMembers,
      entityId,
      entityType,
      updateType = 'updateOrganizationalUnitEntities',
    }: UpdateOrganizationalUnitsParams) {
      return updateOrganizationalUnits({
        addedMembers,
        deletedMembers,
        entityId,
        entityType,
        updateType,
      });
    },
    ...options,
  });
